import { extractBalanceSheetV1 } from "./balance-sheet/v1.extractor";
import { extractBlankV1 } from "./blank/v1.extractor";
import { extractCashFlowStatementV1 } from "./cash-flow-statement/v1.extractor";
import { extractChangeInCapitalV1 } from "./change-in-capital/v1.extractor";
import { ExtractInput } from "./extract-input";
import { ExtractOutput } from "./extract-output";
import { extractIncomeStatementV1 } from "./income-statement/v1.extractor";
import { extractJournalV1 } from "./journal/v1.extractor";
import { extractSelectV1 } from "./select/v1.extractor";
import { extractSingleIncomeStatementV1 } from "./single-income-statement/v1.extractor";
import { extractTAccountV1 } from "./t-account/v1.extractor";
import { extractTrialBalanceV1 } from "./trial-balance/v1.extractor";

/**
 * extractSheet
 *
 * ExtractInput을 받아 questionType과 version에 따라
 * 알맞은 extractor를 호출하여 ExtractOutput을 반환합니다.
 *
 * 📌 사용 목적:
 * - 문제 유형(예: JOURNAL)과 버전(예: v1)에 따라 다르게 파싱/추출하도록 추상화
 * - 채점 전에 학생의 입력 데이터를 일관된 ExtractOutput 형태로 변환
 *
 * @param extractInput - ExtractInput (questionType, version, div 포함)
 * @returns ExtractOutput<any> - 추출된 데이터 및 메타 정보
 * @throws 지원하지 않는 유형/버전일 경우 에러 발생
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractSheet(extractInput: ExtractInput): ExtractOutput<any> {
  const { questionType, version } = extractInput;

  /**
   * 1️⃣ JOURNAL 문제 타입이며 v1 버전인 경우
   * - v1 extractor를 사용해 추출
   */
  if (questionType === "JOURNAL" && version === "v1") {
    return extractJournalV1(extractInput);
  }

  if (questionType === "BALANCE_SHEET" && version === "v1") {
    return extractBalanceSheetV1(extractInput);
  }

  if (questionType === "CASH_FLOW_STATEMENT" && version === "v1") {
    return extractCashFlowStatementV1(extractInput);
  }

  if (questionType === "INCOME_STATEMENT" && version === "v1") {
    return extractIncomeStatementV1(extractInput);
  }

  if (questionType === "SINGLE_INCOME_STATEMENT" && version === "v1") {
    return extractSingleIncomeStatementV1(extractInput);
  }

  if (questionType === "CHANGE_IN_CAPITAL" && version === "v1") {
    return extractChangeInCapitalV1(extractInput);
  }

  if (questionType === "TRIAL_BALANCE" && version === "v1") {
    return extractTrialBalanceV1(extractInput);
  }

  if (questionType === "T_ACCOUNT" && version === "v1") {
    return extractTAccountV1(extractInput);
  }

  if (questionType === "BLANK" && version === "v1") {
    return extractBlankV1(extractInput);
  }

  if (questionType === "SELECT" && version === "v1") {
    return extractSelectV1(extractInput);
  }

  /**
   * 2️⃣ 추후 다른 문제 유형/버전이 추가되면 아래처럼 확장 가능:
   *
   * if (questionType === "LEDGER" && version === "v1") {
   *   return extractLedgerV1(extractInput);
   * }
   */

  /**
   * 3️⃣ 지원하지 않는 문제 유형/버전이면 에러 발생
   * - 관리자가 문제 등록 시 잘못된 유형/버전을 설정한 경우 디버깅 가능
   */
  throw new Error(
    `지원하지 않는 추출 유형 또는 버전입니다: type=${questionType}, version=${version}`,
  );
}
